/**
 * useCategoryTree — TanStack Query data layer for the marketplace category tree
 * (STORY-003-007 step "data layer").
 *
 * Server state (the whole nested category tree) lives in TanStack Query; the
 * tree is small (~250 leaves per PRD §11.7-A) and changes rarely, so it is
 * cached client-side for 60 s (`staleTime`) with a 5-minute GC window
 * (`gcTime`) — exactly the budget the story prescribes.
 *
 * The shape mirrors the backend `CategoryTreeNode` (STORY-003-001,
 * `apps/api/garden/features/marketplace/schemas.py`). That serializer returns
 *   { id, slug, name_ru, name_ky (nullable), name_en (nullable), sort_order,
 *     depth, children[] }
 * `name_ky` / `name_en` fall back to `name_ru` when missing or whitespace-only
 * per REQ-PRD-§13.2-A / ADR-028 (RU/KY/EN all first-class at MVP).
 *
 * No PII travels here — category names are public taxonomy labels.
 */
import { useMemo } from "react";
import {
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

/**
 * One node of the public category tree (mirrors backend `CategoryTreeNode`).
 * `name_ky` / `name_en` are nullable server-side; consumers fall back to
 * `name_ru` (REQ-PRD-§13.2-A). `children` is always present (defaults to `[]`).
 */
export interface Category {
  id: string;
  slug: string;
  name_ru: string;
  name_ky?: string | null;
  name_en?: string | null;
  sort_order: number;
  depth: number;
  children: Category[];
}

/** Response of `GET /marketplace/categories` (mirrors `CategoryTreeResponse`). */
export interface CategoryTreeResponse {
  tree: Category[];
}

/**
 * Pick the localized display name for a category by current language, falling
 * back to RU whenever the chosen field is missing or whitespace-only
 * (REQ-PRD-§13.2-A; story Risks note: "check `name_ky?.trim()` not just
 * `name_ky`"). ADR-028: RU/KY/EN are all first-class; RU is the fallback
 * locale whenever a KY or EN key is missing.
 */
export function categoryDisplayName(node: Category, language: string): string {
  const lang = language.split("-")[0]; // normalize "ru-RU" -> "ru"
  if (lang === "ky") {
    const ky = node.name_ky?.trim();
    if (ky) return ky;
  }
  if (lang === "en") {
    const en = node.name_en?.trim();
    if (en) return en;
  }
  return node.name_ru; // RU fallback (REQ-PRD-§13.2-A)
}

/** The marketplace categories endpoint (public, STORY-003-001). */
export const CATEGORIES_PATH = "/marketplace/categories";

/** Stable query key so other surfaces (browse, search) share one cache entry. */
export function categoryTreeQueryKey(): readonly unknown[] {
  return ["marketplace", "categories"];
}

/** Cache budgets from the story Implementation logic ("API" section). */
export const CATEGORY_TREE_STALE_TIME_MS = 60_000;
export const CATEGORY_TREE_GC_TIME_MS = 300_000;

/**
 * Depth-first search for a node by id. Pure helper (no React) so it can be
 * unit-tested and memoised by the hook.
 */
export function findNodeInTree(
  tree: readonly Category[],
  categoryId: string,
): Category | null {
  for (const node of tree) {
    if (node.id === categoryId) return node;
    const found = findNodeInTree(node.children, categoryId);
    if (found !== null) return found;
  }
  return null;
}

/**
 * Walk from the root down to `categoryId`, returning the full ancestor chain
 * (root first, target last). Empty array when the id is not in the tree.
 * This is how the breadcrumb reconstructs "Строительство → Краска → …" without
 * a separate parent-pointer fetch — the nesting already encodes the path.
 */
export function findPathInTree(
  tree: readonly Category[],
  categoryId: string,
): Category[] {
  for (const node of tree) {
    if (node.id === categoryId) return [node];
    const childPath = findPathInTree(node.children, categoryId);
    if (childPath.length > 0) return [node, ...childPath];
  }
  return [];
}

export interface UseCategoryTreeResult {
  /** The nested tree (empty array until the query resolves). */
  tree: Category[];
  isLoading: boolean;
  error: Error | null;
  /** Refetch the tree (wired to the error-state "Retry" button, AC#10). */
  refetch: UseQueryResult<CategoryTreeResponse, Error>["refetch"];
  /** Memoised ancestor-chain lookup (root → target). */
  findPath: (categoryId: string) => Category[];
  /** Memoised single-node lookup. */
  findNode: (categoryId: string) => Category | null;
}

/**
 * Fetch + cache the category tree and expose memoised path/node lookups.
 *
 * `findPath` / `findNode` are memoised against the resolved tree so the
 * breadcrumb and the auto-expand logic don't re-walk the tree on every render
 * (story Performance note: "memoised via useMemo").
 */
export function useCategoryTree(): UseCategoryTreeResult {
  const query = useQuery<CategoryTreeResponse, Error>({
    queryKey: categoryTreeQueryKey(),
    queryFn: (): Promise<CategoryTreeResponse> =>
      apiClient.get<CategoryTreeResponse>(CATEGORIES_PATH),
    staleTime: CATEGORY_TREE_STALE_TIME_MS,
    gcTime: CATEGORY_TREE_GC_TIME_MS,
  });

  const tree = useMemo<Category[]>(() => query.data?.tree ?? [], [query.data]);

  const findPath = useMemo(
    () =>
      (categoryId: string): Category[] =>
        findPathInTree(tree, categoryId),
    [tree],
  );

  const findNode = useMemo(
    () =>
      (categoryId: string): Category | null =>
        findNodeInTree(tree, categoryId),
    [tree],
  );

  return {
    tree,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    findPath,
    findNode,
  };
}
